import { pickOrigin } from './originPick';
import { defaultAppearance, KARMA_LABELS, LEGEND, lookOf, rankFor, SCHOOLS, type PlayerProgress, type Sex } from '@nine/shared';
import { artH, artW, artX, artY } from '../render/pixel';
import { viewOf, type SpriteBank } from '../render/sprites';
import {
  lastUser,
  loadSave,
  rememberUser,
  session,
  setSession,
  SLOTS,
  writeSave,
  clearSave,
} from '../storage';
import { newPlayer } from '../newPlayer';
import { $, h } from './dom';
import { setFocus, stage as frontStage } from '../front/boot';
import { SCENES } from '../front/scenes';
import { frontAudio } from '../front/audio';
import { drawLogo } from '../front/logo';
import { FRONT_F, FRONT_M } from '../front/looks';
import { arrowNav, escBack, frontSteps } from '../front/ui-steps';

/**
 * The screens before the world: login, server choice and character select. There is no server
 * yet, so the account lives in this browser (no password is kept) and every server but the first
 * is shown as opening soon; the flow is the one the online game will have.
 */
export interface FrontHooks {
  sprites: SpriteBank;
  /** The save slot the world was booted with. */
  loadedSlot: number;
  /** Enter the world with the loaded character. */
  start(): void;
}

interface ServerInfo {
  id: string;
  name: string;
  desc: string;
  open: boolean;
  tag: string;
}

export const SERVERS: readonly ServerInfo[] = [
  {
    id: 's1',
    name: 'ศิลาเก้ากระบี่',
    desc: 'เซิร์ฟเวอร์ทดลอง · เล่นแบบออฟไลน์ในเบราว์เซอร์นี้ได้ทันที',
    open: true,
    tag: 'ทดลอง',
  },
  {
    id: 's2',
    name: 'ยอดเขาหิมะ',
    desc: 'เซิร์ฟเวอร์หลัก · ภารกิจ สำนัก และศึกชิงฉายาเถาฉางเยี่ย',
    open: false,
    tag: 'เร็วๆ นี้',
  },
  {
    id: 's3',
    name: 'สนามโลหิต',
    desc: 'เซิร์ฟเวอร์ PvP · เปิดแดงได้ทั้งแผนที่ เว้นเมืองหลวง',
    open: false,
    tag: 'เร็วๆ นี้',
  },
];

let showT = 0;
const previews: { canvas: HTMLCanvasElement; look: { sprite: string; outfit?: string[] }; ph: number; on?: () => boolean }[] = [];
const lookFor = (pr: PlayerProgress): { sprite: string; outfit?: string[] } =>
  pr.appearance?.sex === 'f' ? { ...FRONT_F } : lookOf(pr);

export function runFront(hk: FrontHooks): void {
  const root = $('#front');
  const s = session();
  // The title, the legend and the build line stay; the screens change in the slot between them.
  const slot = h('div', { class: 'fr-slot' });
  const logo = h('canvas', { class: 'px-logo fr-logo' });
  void drawLogo(logo as HTMLCanvasElement, true);
  const mute = h('button', { type: 'button', class: 'fr-mute' });
  const paintMute = () => {
    mute.textContent = frontAudio.muted ? 'เสียง: ปิด' : 'เสียง: เปิด';
    mute.setAttribute('aria-pressed', String(frontAudio.muted));
  };
  paintMute();
  mute.addEventListener('click', () => {
    frontAudio.toggle();
    paintMute();
  });
  // The lore line sits in the card's column (the side the picture keeps free), never over the figures.
  const legend = h('p', { class: 'fr-legend px-box' }, LEGEND[0]!.text[0] ?? '');
  // Each scene has its own line (scenes/index.ts); the legend's first line until a scene is up.
  const caption = (id: string | undefined) => {
    legend.textContent = SCENES.find((d) => d.id === id)?.caption ?? LEGEND[0]!.text[0] ?? '';
  };
  caption(document.documentElement.dataset.scene);
  if (frontStage) {
    const prevScene = frontStage.onScene;
    frontStage.onScene = (id) => {
      prevScene?.(id);
      caption(id);
    };
  }
  root.replaceChildren(
    h(
      'div',
      { class: 'fr-stage' },
      logo,
      h('div', { class: 'fr-side' }, legend, slot),
      mute,
      h('p', { class: 'fr-ver' }, 'เวอร์ชันทดลอง · PHASE 1'),
    ),
  );
  const show = (screen: HTMLElement) => {
    slot.replaceChildren(screen);
    root.dataset.screen = screen.dataset.screen ?? '';
    setFocus('side');
    root.hidden = false;
  };
  const enter = (slot: number) => {
    setSession({ slot, enter: true });
    if (slot === hk.loadedSlot) {
      root.hidden = true;
      previews.length = 0;
      hk.start();
    } else location.reload();
  };

  const login = (): HTMLElement => {
    const user = h('input', { type: 'text', maxLength: 16, value: lastUser(), autocomplete: 'username' });
    const pass = h('input', { type: 'password', maxLength: 32, autocomplete: 'current-password' });
    const msg = h('p', { class: 'fr-msg' });
    const go = (name: string) => {
      rememberUser(name);
      setSession({ user: name });
      show(servers());
    };
    const submit = h('button', { type: 'submit', class: 'btn' }, 'เข้าสู่ระบบ');
    const form = h(
      'form',
      { class: 'fr-form' },
      h('label', { class: 'fr-field' }, 'ชื่อบัญชี', user),
      h('label', { class: 'fr-field' }, 'รหัสผ่าน', pass),
      msg,
      submit,
      h(
        'button',
        { type: 'button', class: 'btn ghost', onclick: () => go('ผู้เยี่ยมชม') },
        'เล่นแบบผู้เยี่ยมชม',
      ),
      h(
        'div',
        { class: 'fr-links' },
        h('span', { class: 'fr-soon', title: 'เปิดพร้อมเซิร์ฟเวอร์' }, 'สมัครสมาชิก (เร็วๆ นี้)'),
        h('a', { href: './site/index.html', target: '_blank' }, 'เว็บไซต์ทางการ'),
      ),
    );
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = user.value.trim();
      if (name.length < 2) {
        msg.textContent = 'กรอกชื่อบัญชีอย่างน้อย 2 ตัวอักษร';
        return;
      }
      if (!pass.value) {
        msg.textContent = 'กรอกรหัสผ่าน';
        return;
      }
      go(name);
    });
    setTimeout(() => (user.value ? pass : user).focus(), 0);
    return h(
      'section',
      { class: 'fr-screen fr-login', data: { screen: 'login' } },
      h(
        'div',
        { class: 'fr-card px-box' },
        frontSteps(0),
        h('h2', {}, 'เข้าสู่ยุทธภพ'),
        form,
        h(
          'p',
          { class: 'fr-note' },
          'ช่วงพัฒนา: ยังไม่เปิดเซิร์ฟเวอร์ บัญชีและตัวละครเก็บไว้ในเบราว์เซอร์นี้เท่านั้น (ไม่เก็บรหัสผ่าน)',
        ),
      ),
    );
  };

  const logout = () => {
    setSession({ user: null, server: null });
    show(login());
  };
  const servers = (): HTMLElement => {
    const screen = h(
      'section',
      { class: 'fr-screen', data: { screen: 'servers' } },
      h(
        'div',
        { class: 'fr-card px-box' },
        frontSteps(1),
        h('h2', {}, 'เลือกเซิร์ฟเวอร์'),
        h(
          'div',
          { class: 'fr-servers' },
          ...SERVERS.map((sv) => {
            const b = h(
              'button',
              { type: 'button', class: `fr-server ${sv.open ? '' : 'closed'}`, disabled: !sv.open },
              h('span', { class: `fr-dot ${sv.open ? 'on' : ''}` }),
              h('b', {}, sv.name),
              h('em', {}, sv.tag),
              h('small', {}, sv.desc),
            );
            b.addEventListener('click', () => {
              setSession({ server: sv.id });
              show(chars());
            });
            return b;
          }),
        ),
        h(
          'div',
          { class: 'fr-row' },
          h(
            'button',
            { type: 'button', class: 'btn ghost small', onclick: logout },
            'ออกจากระบบ',
          ),
          h('span', { class: 'fr-user' }, `บัญชี: ${session().user ?? ''}`),
        ),
      ),
    );
    arrowNav(screen, '.fr-server');
    escBack(screen, logout);
    setTimeout(() => screen.querySelector<HTMLElement>('.fr-server:not(:disabled)')?.focus(), 0);
    return screen;
  };

  const chars = (): HTMLElement => {
    previews.length = 0;
    const sv = SERVERS.find((x) => x.id === session().server) ?? SERVERS[0]!;
    const list = h('div', { class: 'fr-chars' });
    for (let slot = 0; slot < SLOTS; slot++) list.append(charCard(slot));
    const screen = h(
      'section',
      { class: 'fr-screen', data: { screen: 'chars' } },
      h(
        'div',
        { class: 'fr-card px-box' },
        frontSteps(2),
        h('h2', {}, 'เลือกตัวละคร', h('small', { class: 'fr-sub' }, sv.name)),
        list,
        h(
          'div',
          { class: 'fr-row' },
          h(
            'button',
            { type: 'button', class: 'btn ghost small', onclick: () => show(servers()) },
            'เปลี่ยนเซิร์ฟเวอร์',
          ),
          h('span', { class: 'fr-user' }, `บัญชี: ${session().user ?? ''}`),
        ),
      ),
    );
    arrowNav(list, '.fr-char .btn:not(.ghost)');
    escBack(screen, () => show(servers()));
    setTimeout(() => list.querySelector<HTMLElement>('.fr-char .btn')?.focus(), 0);
    return screen;
  };

  const charCard = (slot: number): HTMLElement => {
    const pr = loadSave(slot);
    if (!pr) return emptyCard(slot);
    const pic = h('canvas', { class: 'fr-pic', width: 48, height: 64 });
    previews.push({ canvas: pic, look: lookFor(pr), ph: previews.length });
    const play = h('button', { type: 'button', class: 'btn', onclick: () => enter(slot) }, 'เข้าเกม');
    let armed = false;
    const del = h('button', { type: 'button', class: 'btn ghost small' }, 'ลบ');
    del.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        del.textContent = 'ยืนยันลบถาวร';
        return;
      }
      clearSave(slot);
      show(chars());
    });
    return h(
      'div',
      { class: 'fr-char' },
      pic,
      h('div', { class: 'fr-title' }, pr.title),
      h('div', { class: 'fr-name' }, pr.name),
      h(
        'div',
        { class: 'fr-meta' },
        `${pr.school ? `สำนัก${SCHOOLS[pr.school]?.name ?? ''}` : 'ไร้สำนัก'} · ${rankFor(pr.cultivationSpent).name}`,
      ),
      h('div', { class: 'fr-meta' }, `ฝ่าย${KARMA_LABELS[pr.karma]} · ค่า PK ${pr.pkPoints}`),
      h('div', { class: 'fr-actions' }, play, del),
    );
  };

  const emptyCard = (slot: number): HTMLElement => {
    const card = h('div', { class: 'fr-char empty' });
    card.append(
      h('div', { class: 'fr-plus' }, '+'),
      h('div', { class: 'fr-meta' }, `ช่องที่ ${slot + 1} · ว่าง`),
      h('button', { type: 'button', class: 'btn', onclick: () => show(create(slot)) }, 'สร้างตัวละคร'),
    );
    return card;
  };

  /** Character creation: a man or a woman, each on a pedestal, then a name. */
  const create = (slot: number): HTMLElement => {
    previews.length = 0;
    let sex: Sex = 'm';
    const name = h('input', { type: 'text', maxLength: 12, autocomplete: 'off', placeholder: 'ชื่อตัวละคร' });
    const msg = h('p', { class: 'fr-msg' });
    const pick = (k: Sex, label: string, look: { sprite: string; outfit?: string[] }) => {
      const pic = h('canvas', { class: 'fr-pic big', width: 72, height: 96 });
      previews.push({ canvas: pic, look, ph: k === 'm' ? 0 : 1.7 });
      const b = h(
        'button',
        { type: 'button', class: `fr-sex ${k === sex ? 'on' : ''}` },
        pic,
        h('b', {}, label),
      );
      b.setAttribute('aria-pressed', String(k === sex));
      b.addEventListener('click', () => {
        sex = k;
        for (const x of box.querySelectorAll('.fr-sex')) {
          x.classList.toggle('on', x === b);
          x.setAttribute('aria-pressed', String(x === b));
        }
      });
      return b;
    };
    const box = h(
      'div',
      { class: 'fr-sexes' },
      pick('m', 'จอมยุทธ์ชาย', { ...FRONT_M, outfit: [...FRONT_M.outfit] }),
      pick('f', 'จอมยุทธ์หญิง', { ...FRONT_F }),
    );
    const form = h(
      'form',
      { class: 'fr-form' },
      box,
      h('label', { class: 'fr-field' }, 'ชื่อตัวละคร', name),
      msg,
      h('button', { type: 'submit', class: 'btn' }, 'สร้างและเข้าเกม'),
      h('button', { type: 'button', class: 'btn ghost small', onclick: () => show(chars()) }, 'ย้อนกลับ'),
    );
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const n = name.value.trim();
      if (n.length < 2) {
        msg.textContent = 'ชื่อตัวละครอย่างน้อย 2 ตัวอักษร';
        return;
      }
      const pr = newPlayer();
      pr.name = n;
      pr.appearance = defaultAppearance(sex);
      pickOrigin(pr, () => {
        writeSave(pr, slot);
        setSession({ slot, enter: true });
        location.reload();
      });
    });
    arrowNav(box, '.fr-sex');
    setTimeout(() => name.focus(), 0);
    const screen = h(
      'section',
      { class: 'fr-screen', data: { screen: 'create' } },
      h(
        'div',
        { class: 'fr-card px-box' },
        frontSteps(2),
        h('h2', {}, 'สร้างตัวละคร', h('small', { class: 'fr-sub' }, `ช่องที่ ${slot + 1}`)),
        form,
      ),
    );
    escBack(screen, () => show(chars()));
    return screen;
  };

  // Straight back into the world after picking a character (the page reloads to load it).
  if (s.user && s.enter && s.slot === hk.loadedSlot && loadSave(s.slot)) {
    root.hidden = true;
    hk.start();
    return;
  }
  show(!s.user ? login() : !s.server ? servers() : chars());
}

/** Each hero on the select and create screens standing on an isometric pedestal, idling, turned to the front. */
export function drawFrontPreviews(sprites: SpriteBank, dt: number): void {
  if (!previews.length) return;
  showT += dt;
  previews.forEach(({ canvas, look, ph }) => {
    const base = sprites.of(look);
    const g = canvas.getContext('2d')!;
    g.clearRect(0, 0, canvas.width, canvas.height);
    g.imageSmoothingEnabled = false;
    const cx = canvas.width >> 1;
    const by = canvas.height - 10;
    // The pedestal: a stone diamond, two faces, with a gold rim.
    const w = canvas.width * 0.42;
    g.fillStyle = '#2a2638';
    g.beginPath(); g.moveTo(cx - w, by); g.lineTo(cx, by + w / 2); g.lineTo(cx, by + w / 2 + 5); g.lineTo(cx - w, by + 5); g.fill();
    g.fillStyle = '#3d374f';
    g.beginPath(); g.moveTo(cx + w, by); g.lineTo(cx, by + w / 2); g.lineTo(cx, by + w / 2 + 5); g.lineTo(cx + w, by + 5); g.fill();
    g.fillStyle = '#6a6578';
    g.beginPath(); g.moveTo(cx - w, by); g.lineTo(cx, by - w / 2); g.lineTo(cx + w, by); g.lineTo(cx, by + w / 2); g.fill();
    g.strokeStyle = '#d9b46c';
    g.lineWidth = 1;
    g.beginPath(); g.moveTo(cx - w + 2, by); g.lineTo(cx, by - w / 2 + 1); g.lineTo(cx + w - 2, by); g.lineTo(cx, by + w / 2 - 1); g.closePath(); g.stroke();
    if (!base) return;
    const { set, facing } = viewOf(base, 2, 1);
    const img = set.idle[Math.floor((showT * 2.6 + ph) % set.idle.length)];
    if (!img) return;
    g.save();
    g.translate(cx, by + 2);
    if (facing < 0) g.scale(-1, 1);
    g.drawImage(img, artX(img) - set.ax, artY(img) - set.ay, artW(img), artH(img));
    g.restore();
  });
}
